import {
  Box,
  Button,
  CircularProgress,
  Radio,
  Typography,
} from "@mui/material";
import React, { useState, useEffect } from "react";
import CustomModal from "../../component/common/customModal";
import invoiceService from "../../../services/InvoiceService";
import { CURRENCY_SYMBOL } from "../../../constants/currency";
import CardPaymentForm from "./CardPaymentForm";
import { useTranslation } from "react-i18next";
import { showToast } from "../../component/common/toastNotification";
import { buildPaymentCallbackUrl } from "../../../utils/paymentCallbackUrl";
import CreditCardOutlinedIcon from "@mui/icons-material/CreditCardOutlined";
import AssuredWorkloadOutlinedIcon from "@mui/icons-material/AssuredWorkloadOutlined";
import MobileFriendlyOutlinedIcon from "@mui/icons-material/MobileFriendlyOutlined";
import PaymentOutlinedIcon from "@mui/icons-material/PaymentOutlined";
import CustomInput from "../../component/common/inputField";
import {
  mapQiCardPaymentError,
  startQiCardWalletRecharge,
} from "../../../utils/qicardPayFlow";
import { WalletType } from "../../../types/dashboardType";
import { formatWalletDisplay } from "../../../helper/formatAppBalance";
import { NJIM } from "../../../theme/njimTheme";

interface Props {
  open: boolean;
  onClose: () => void;
  WalletData: WalletType;
}


const monoFont = 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace';

const TopUpWalletModal: React.FC<Props> = ({ open, onClose, WalletData }) => {
  const { t, i18n } = useTranslation();
  const [method, setMethod] = useState("card");
  const [loading, setLoading] = useState(false);
  const [CardPaymentModal, setCardPaymentModal] = useState(false);
  const [checkoutId, setCheckoutId] = useState("");
  const [topUpAmount, setTopUpAmount] = useState<string>("");
  const [amountError, setAmountError] = useState<string>("");

  const CARD_MIN_AMOUNT = 10;
  const ZAINCASH_MIN_AMOUNT = 250;
  const minAmount = method === "mobile" ? ZAINCASH_MIN_AMOUNT : CARD_MIN_AMOUNT;

  useEffect(() => {
    if (open) {
      setTopUpAmount("");
      setAmountError("");
      setMethod("card");
    }
  }, [open]);

  const validateAmount = (amount: string, paymentMethod = method): boolean => {
    const numAmount = Number(amount);
    const minimum = paymentMethod === "mobile" ? ZAINCASH_MIN_AMOUNT : CARD_MIN_AMOUNT;
    if (!amount || numAmount <= 0) {
      setAmountError(t("invoicesPayments.pleaseEnterTopUpAmount"));
      return false;
    }
    if (numAmount < minimum) {
      setAmountError(
        `${t("invoicesPayments.minimumAmountIs")} ${CURRENCY_SYMBOL}${minimum.toFixed(2)}`
      );
      return false;
    }
    setAmountError("");
    return true;
  };

  useEffect(() => {
    if (!open || !topUpAmount) return;
    validateAmount(topUpAmount, method);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [method]);

  const handleConfirmTopUp = async () => {
    if (!validateAmount(topUpAmount)) {
      return;
    }

    if (method === "qicard") {
      setLoading(true);
      try {
        await startQiCardWalletRecharge({
          amount: Number(topUpAmount),
          language: i18n.language,
        });
      } catch (err) {
        const code = mapQiCardPaymentError(err);
        if (code === "missing_redirect") {
          showToast(t("qicard.missingRedirect"), "error");
        } else {
          showToast(t("invoicesPayments.paymentFailed"), "error");
        }
        setLoading(false);
      }
      return;
    }

    setLoading(true);

    const getPaymentType = () => {
      if (method === "mobile") return "ZainCash";
      return "CardPayment";
    };

    const redirectUrl = buildPaymentCallbackUrl({
      returnTo: "wallet",
      type: getPaymentType(),
    });

    const inputData = {
      amount: Number(topUpAmount),
      redirectUrl: redirectUrl,
      successUrl: redirectUrl,
      failedUrl: redirectUrl,
      language: i18n.language?.startsWith("ar") ? "ar" : "en",
      externalReferenceId:
        typeof crypto !== "undefined" && "randomUUID" in crypto
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random().toString(16).slice(2)}`,
      type: getPaymentType(),
    };

    try {
      const response = await invoiceService.WalletPaymentCreate(inputData);

      if (response.data?.redirectUrl) {
        if (getPaymentType() === "ZainCash") {
          localStorage.setItem("lastZainCashWalletTopUp", "1");
          if (response.data?.transactionId) {
            localStorage.setItem(
              "lastZainCashTransactionId",
              String(response.data.transactionId)
            );
          }
        }
        window.location.href = response.data.redirectUrl;
      } else if (response.data && method === "card") {
        const txId = String(response.data.transactionId ?? "");
        if (txId) {
          localStorage.setItem("lastCardTopUpTransactionId", txId);
        }
        setCardPaymentModal(true);
        setCheckoutId(txId);
      } else {
        showToast(t("invoicesPayments.paymentFailed"), "error");
      }
    } catch {
      showToast(t("invoicesPayments.paymentFailed"), "error");
    } finally {
      setLoading(false);
    }
  };

  const paymentRedirectUrl = checkoutId
    ? buildPaymentCallbackUrl({
        returnTo: "wallet",
        type: "CardPayment",
        checkoutId,
      })
    : buildPaymentCallbackUrl({ returnTo: "wallet", type: "CardPayment" });

  const confirmLabel = topUpAmount
    ? `${t("invoicesPayments.confirmTopUp")} - ${CURRENCY_SYMBOL}${Number(topUpAmount).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`
    : t("invoicesPayments.confirmTopUp");

  const paymentOptions = [
    {
      label: t("invoicesPayments.creditDebitCard"),
      sub: t("invoicesPayments.payWithCard"),
      value: "card",
      icon: CreditCardOutlinedIcon,
    },
    {
      label: t("invoicesPayments.qiCard"),
      sub: t("invoicesPayments.payWithQiCard"),
      value: "qicard",
      icon: PaymentOutlinedIcon,
    },
    {
      label: t("invoicesPayments.bankTransfer"),
      sub: t("invoicesPayments.directBankTransfer"),
      value: "bank",
      disabled: true,
      icon: AssuredWorkloadOutlinedIcon,
    },
    {
      label: t("invoicesPayments.mobileWallet"),
      sub: t("invoicesPayments.payViaMobileWallet"),
      value: "mobile",
      icon: MobileFriendlyOutlinedIcon,
    },
  ];

  return (
    <>
      <CustomModal
        open={open}
        onClose={onClose}
        ModalTitle={t("invoicesPayments.topUpWallet")}
        ModalDescription={t("invoicesPayments.addFundsToWallet")}
        width={{ xs: "94%", sm: 560, md: 620 }}
        bgcolor={NJIM.white}
        paperSx={{
          p: { xs: 3, sm: 4 },
          borderRadius: "16px",
          border: `1px solid ${NJIM.border}`,
          boxShadow: NJIM.elevation,
          maxWidth: "100%",
        }}
        titleSx={{
          fontSize: { xs: 20, sm: 22 },
          fontWeight: 600,
          color: NJIM.textPrimary,
          letterSpacing: "-0.02em",
        }}
        descriptionSx={{
          fontSize: { xs: 14, sm: 15 },
          color: NJIM.textSecondary,
          mt: 0.75,
          mb: 0.5,
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: { xs: 3, sm: 3.5 }, mt: 3 }}>
          {/* Current balance */}
          <Box
            sx={{
              p: { xs: 2, sm: 2.5 },
              bgcolor: NJIM.muted,
              borderRadius: "12px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
            }}
          >
            <Typography sx={{ fontSize: { xs: 14, sm: 15 }, color: NJIM.textSecondary }}>
              {t("invoicesPayments.currentBalance")}
            </Typography>
            <Typography
              sx={{
                fontFamily: monoFont,
                fontSize: { xs: 18, sm: 22 },
                fontWeight: 600,
                color: NJIM.mintForeground,
                textAlign: "right",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {formatWalletDisplay(WalletData?.balance, "IQD")}
            </Typography>
          </Box>

          {/* Amount input */}
          <Box
            sx={{
              "& .MuiOutlinedInput-root": {
                height: { xs: 48, sm: 52 },
                fontSize: { xs: 15, sm: 16 },
              },
              "& .MuiOutlinedInput-root:hover fieldset, & .MuiOutlinedInput-root.Mui-focused fieldset": {
                border: `2px solid ${NJIM.mint} !important`,
                borderRadius: "10px",
              },
              "& .MuiTypography-subtitle1": {
                fontSize: { xs: 14, sm: 15 },
                fontWeight: 500,
              },
            }}
          >
            <CustomInput
              label={`${t("invoicesPayments.topUpAmount")} (${CURRENCY_SYMBOL})`}
              placeholder={t("invoicesPayments.enterAmount")}
              name="topUpAmount"
              value={topUpAmount}
              type="number"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setTopUpAmount(e.target.value);
                if (amountError) {
                  validateAmount(e.target.value);
                }
              }}
              onBlur={() => validateAmount(topUpAmount)}
              backgroundColor={NJIM.muted}
              error={amountError}
              marginBottom="4px"
            />
            {!amountError && (
              <Typography sx={{ fontSize: { xs: 12, sm: 13 }, color: NJIM.textSecondary, mt: 0.75 }}>
                {t("invoicesPayments.minimum")} {CURRENCY_SYMBOL}
                {minAmount.toFixed(2)}
                {method === "mobile" ? ` (${t("invoicesPayments.zainCashMinimumHint")})` : ""}
              </Typography>
            )}
          </Box>

          {/* Payment methods */}
          <Box>
            <Typography
              sx={{
                fontWeight: 500,
                fontSize: { xs: 15, sm: 16 },
                color: NJIM.textPrimary,
                mb: 1.75,
              }}
            >
              {t("invoicesPayments.selectPaymentMethodTitle")}
            </Typography>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
              {paymentOptions.map((item) => {
                const Icon = item.icon;
                const isSelected = method === item.value;
                return (
                  <Box
                    key={item.value}
                    onClick={() => !item.disabled && setMethod(item.value)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.75,
                      p: { xs: 1.75, sm: 2 },
                      borderRadius: "12px",
                      border: `1px solid ${isSelected ? NJIM.mint : NJIM.border}`,
                      bgcolor: isSelected ? NJIM.mintWash : NJIM.white,
                      cursor: item.disabled ? "default" : "pointer",
                      opacity: item.disabled ? 0.5 : 1,
                      transition: "background-color 180ms ease, border-color 180ms ease",
                      "&:hover": {
                        bgcolor: item.disabled ? undefined : NJIM.muted,
                        borderColor: item.disabled ? undefined : NJIM.mint,
                      },
                    }}
                  >
                    <Radio
                      checked={isSelected}
                      onChange={() => !item.disabled && setMethod(item.value)}
                      disabled={item.disabled}
                      size="medium"
                      sx={{
                        p: 0.5,
                        color: NJIM.border,
                        "&.Mui-checked": { color: NJIM.mint },
                      }}
                    />
                    <Icon sx={{ fontSize: 22, color: NJIM.mint, flexShrink: 0 }} />
                    <Box sx={{ minWidth: 0 }}>
                      <Typography
                        sx={{ fontWeight: 500, fontSize: { xs: 14, sm: 15 }, color: NJIM.textPrimary, lineHeight: 1.3 }}
                      >
                        {item.label}
                      </Typography>
                      <Typography sx={{ fontSize: { xs: 12, sm: 13 }, color: NJIM.textSecondary, lineHeight: 1.3 }}>
                        {item.sub}
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Box>

          {/* Confirm */}
          <Button
            className="topup-confirm-btn"
            variant="contained"
            fullWidth
            disableElevation
            sx={{
              fontSize: { xs: 15, sm: 16 },
              py: { xs: 1.5, sm: 1.75 },
            }}
            onClick={handleConfirmTopUp}
            disabled={!topUpAmount || loading}
          >
            {loading ? (
              <CircularProgress size={24} sx={{ color: NJIM.white }} />
            ) : (
              confirmLabel
            )}
          </Button>
        </Box>
      </CustomModal>

      {CardPaymentModal && (
        <CardPaymentForm
          open={CardPaymentModal}
          onClose={() => setCardPaymentModal(false)}
          checkoutId={checkoutId}
          action={paymentRedirectUrl}
          amount={Number(topUpAmount || 0)}
          currency="IQD"
          transactionStorageKey="lastCardTopUpTransactionId"
        />
      )}
    </>
  );
};

export default TopUpWalletModal;
